import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import * as Sentry from '@sentry/capacitor';
import { supabaseAuthService } from '../services/supabase/auth.service';
import { authAnonymousService } from '../services/supabase/auth-anonymous.service';
import { User, AuthState } from '../models';
import { supabase } from '../lib/supabase';
import { clearAccountStorage } from '../lib/clearAccountStorage';
import { useAppContextStore } from './appContext';
import { useUserContextStore } from './userContext';
import { Capacitor } from '@capacitor/core';
import { useAnalytics } from '@/composables/useAnalytics';
import { feedbackService } from '@/services/feedback.service';
import { initRevenueCat } from '@/services/revenuecat.service';
import * as pushTokenService from '@/services/push-token.service';
import { detectLocale } from '@/i18n';
import type { User as SupabaseAuthUser } from '@supabase/supabase-js';

// A brand new account, on its first SIGNED_IN, has last_sign_in_at either
// missing or essentially equal to created_at. Returning users sign in well
// after their account was created. We treat anything within this window as a
// signup so OAuth (Google/Apple) signups — which never hit the manual email
// signup path — are counted as sign_up rather than login.
const NEW_USER_SIGNIN_WINDOW_MS = 10_000;

function isNewlyCreatedUser(user: SupabaseAuthUser): boolean {
  if (!user.created_at) return false;
  // No prior sign-in recorded → this is the first one → a signup.
  if (!user.last_sign_in_at) return true;
  const createdAt = new Date(user.created_at).getTime();
  const lastSignInAt = new Date(user.last_sign_in_at).getTime();
  if (Number.isNaN(createdAt) || Number.isNaN(lastSignInAt)) return false;
  return Math.abs(lastSignInAt - createdAt) <= NEW_USER_SIGNIN_WINDOW_MS;
}

export const useAuthStore = defineStore('auth', () => {
  // Initialize analytics once
  const analytics = useAnalytics();

  // Guards SIGNED_IN re-firing (token refresh, app resume) so we log
  // sign_up/login at most once per authenticated user per session.
  let lastTrackedSignInUserId: string | null = null;

  const user = ref<User | null>(null);
  const isAuthenticated = ref(false);
  const loading = ref(true);
  const error = ref<string | null>(null);

  // Magic link state
  const isLinkSent = ref(false);
  const lastMagicLinkEmail = ref('');
  const magicLinkContext = ref<'login' | 'signup' | 'recovery' | null>(null);
  const linkCooldown = ref(0);

  // Initialize auth state from localStorage if available
  const storedUser = localStorage.getItem('harmonia_user');
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser);
      isAuthenticated.value = true;
    } catch (e) {
      localStorage.removeItem('harmonia_user');
    }
  }

  /**
   * Pose l'utilisateur depuis la session locale, sans réseau, pour débloquer
   * l'UI tout de suite. Le profil complet (user_profiles) le remplace ensuite,
   * chargé en arrière-plan par le gestionnaire onAuthStateChange.
   * Si l'utilisateur déjà en mémoire (profil complet restauré de localStorage)
   * est le même compte, on le garde : le remplacer par la version basique
   * effaçait le prénom affiché le temps du rechargement.
   */
  function applySessionUser(sessionUser: SupabaseAuthUser) {
    if (user.value?.id !== sessionUser.id) {
      const meta = sessionUser.user_metadata || {};
      const nameFromMeta = meta.display_name || meta.full_name || meta.name;
      user.value = {
        id: sessionUser.id,
        email: sessionUser.email || '',
        // JAMAIS le début de l'adresse e-mail : « Bonjour sebastien.balieu+2 »
        // n'est pas un prénom, c'est un identifiant technique montré à un
        // parent. Une connexion par OTP n'a aucun nom dans ses métadonnées,
        // donc ce repli s'appliquait à TOUS les comptes créés par e-mail.
        // Vide assumé : le vrai prénom arrive du profil quelques
        // millisecondes plus tard, et l'accueil sait saluer sans nom.
        name: nameFromMeta || '',
        photoURL: meta.avatar_url,
        language: detectLocale() as 'fr',
        notification_preferences: {
          daily_reminder: false,
          point_assignments: false,
          reward_unlocked: false
        }
      };
      localStorage.setItem('harmonia_user', JSON.stringify(user.value));
    }
    isAuthenticated.value = true;
    feedbackService.initializeForUser(sessionUser.id, sessionUser.created_at);
    Sentry.setUser({
      id: sessionUser.id,
      username: sessionUser.email ? undefined : 'anonymous',
    });
  }

  // Function to initialize auth listener
  function initAuth() {
    return new Promise<void>(async (resolve) => { // eslint-disable-line no-async-promise-executor -- TODO restructurer sans async-executor pour ne pas avaler d'erreur (test device)
      // Vérifier d'abord la session actuelle
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // On débloque depuis la session LOCALE, sans attendre le réseau. Le
        // profil complet était chargé ici (getUser ×2 + user_profiles, en série,
        // sous le verrou d'auth) avant que le routeur et App.vue puissent
        // avancer, puis rechargé une seconde fois par INITIAL_SESSION juste en
        // dessous. Il n'est plus chargé qu'une fois, là-bas, en arrière-plan
        // (HARMONIA-76). Le routeur ne lit que id et email, présents ici.
        applySessionUser(session.user);
      } else {
        user.value = null;
        isAuthenticated.value = false;
        localStorage.removeItem('harmonia_user');
      }

      // Set up Supabase auth state listener
      supabase.auth.onAuthStateChange(async (event, session) => {
        // console.log('🔐 [Auth Store] Auth state changed:', { event, hasSession: !!session });

        // Track auth events in Analytics
        try {
          if (event === 'SIGNED_IN' && session?.user) {
            // Set user ID in Analytics
            await analytics.setUserId(session.user.id);

            const authMethod = session.user.app_metadata?.provider || 'email';

            // SIGNED_IN fires for every authentication across ALL providers
            // (Google/Apple native + web, email, token refresh, app resume).
            // Supabase has no dedicated SIGNED_UP event, so distinguish a brand
            // new account from a returning login here — otherwise every OAuth
            // signup is miscounted as a login and sign_up stays near-zero
            // (Amplitude showed 3 sign_up vs 2902 login over a year).
            // A fresh account has last_sign_in_at absent or within a few seconds
            // of created_at. Guard against SIGNED_IN re-firing for the same user
            // within a session so we log it at most once.
            if (lastTrackedSignInUserId !== session.user.id) {
              lastTrackedSignInUserId = session.user.id;
              if (isNewlyCreatedUser(session.user)) {
                await analytics.logSignUp(authMethod);
              } else {
                await analytics.logLogin(authMethod);
              }

              // Register the device's push token (if notifications already
              // granted) and listen for FCM token rotations. No-ops on web and
              // when permission hasn't been granted yet — the pre-prompt flow
              // calls registerCurrentToken() again right after a grant.
              void pushTokenService.startTokenRefreshListener();
              void pushTokenService.registerCurrentToken();
            }
          } else if (event === 'SIGNED_OUT') {
            // Clear user ID in Analytics
            await analytics.setUserId(null);
            await analytics.logEvent('logout');
            // Allow the next sign-in (possibly a different user) to be tracked again.
            lastTrackedSignInUserId = null;
          }
        } catch (err) {
          // Analytics might not be initialized yet, that's ok
          console.debug('[Auth Store] Analytics tracking skipped:', err);
        }

        // Cold start restores a persisted session as INITIAL_SESSION, NOT
        // SIGNED_IN, so the registration inside the SIGNED_IN branch above never
        // runs on a plain app relaunch. Register here too (idempotent, no-ops on
        // web / without permission) — otherwise a user whose token row was lost
        // (FCM-pruned, or reclaimed by another account on the device) never
        // re-registers just by reopening the app; they'd have to log out/in.
        if (event === 'INITIAL_SESSION' && session?.user) {
          void pushTokenService.startTokenRefreshListener();
          void pushTokenService.registerCurrentToken();
        }

        // 1. Mettre à jour l'état synchrone immédiatement
        if (session?.user) {
          applySessionUser(session.user);
          initRevenueCat(session.user.id);
        } else {
          user.value = null;
          isAuthenticated.value = false;
          localStorage.removeItem('harmonia_user');
          feedbackService.resetForCurrentUser();
          Sentry.setUser(null);
        }

        loading.value = false; // Débloquer l'UI immédiatement

        // 2. Puis lancer la récupération du profil complet en arrière-plan
        if (session?.user) {
          queueMicrotask(async () => {
            try {
              // console.log('🔄 [Auth Store] Fetching complete user profile in background...');
              const fullProfile = await supabaseAuthService.getCurrentUser();

              if (fullProfile) {
                user.value = fullProfile;
                localStorage.setItem('harmonia_user', JSON.stringify(fullProfile));
                feedbackService.initializeForUser(fullProfile.id);

                // Track user properties in Analytics
                try {
                  if (fullProfile.language) {
                    await analytics.setUserProperty('language', fullProfile.language);
                  }
                } catch (err) {
                  // Analytics might not be initialized yet
                }

                // Note: La vérification des nouveaux utilisateurs Google est maintenant gérée dans useAuthGuard

                // Exécuter les migrations et l'initialisation du profil en arrière-plan
                try {
                  const { initializeMigrations } = await import('@/utils/migration');
                  await initializeMigrations();

                  const { initializeUserProfile } = await import('@/utils/profile-creator');
                  await initializeUserProfile();

                  // console.log('✅ [Auth Store] Background profile and migrations completed');
                } catch (migrationErr) {
                  console.warn('[Auth Store] Background migration/profile init failed:', migrationErr);
                }
              }
            } catch (err) {
              console.error('[Auth Store] Background profile fetch error:', err);
              // Ne pas changer l'état d'auth ici, garder l'utilisateur de base
            }
          });
        }

        resolve();
      });

      // Marquer comme non chargement une fois la vérification initiale terminée
      loading.value = false;
      resolve();
    });
  }

  // Call initAuth immediately and expose the promise
  const authInitialized = initAuth();

  /**
   * Function to check if auth is ready (waits for session restoration)
   * Usage: await authStore.waitForAuth() dans guards ou onMounted
   */
  async function waitForAuth() {
    if (!loading.value) return isAuthenticated.value;
    await authInitialized;
    return isAuthenticated.value;
  }

  /**
   * Helper function to wait for specific auth state (for components)
   * Usage: await authStore.waitForAuthState(true) // wait for authentication
   */
  async function waitForAuthState(expectedAuthState: boolean, timeout = 10000) {
    return new Promise<boolean>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Auth state timeout'));
      }, timeout);

      const checkState = () => {
        if (isAuthenticated.value === expectedAuthState) {
          clearTimeout(timeoutId);
          resolve(isAuthenticated.value);
        }
      };

      // Check immediately
      checkState();

      // Watch for changes
      const stopWatcher = watch(isAuthenticated, () => {
        checkState();
        if (isAuthenticated.value === expectedAuthState) {
          stopWatcher();
        }
      });
    });
  }

  // Helper functions for magic link
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getRedirectUrl(): string | undefined {
    if (Capacitor.isNativePlatform()) {
      return 'com.withharmonia.app://auth/callback';
    }
    return `${window.location.origin}/auth/callback`;
  }

  let cooldownInterval: ReturnType<typeof setInterval> | null = null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function startCooldown(seconds: number) {
    linkCooldown.value = seconds;

    if (cooldownInterval) {
      clearInterval(cooldownInterval);
    }

    cooldownInterval = setInterval(() => {
      linkCooldown.value--;
      if (linkCooldown.value <= 0) {
        clearInterval(cooldownInterval!);
        cooldownInterval = null;
      }
    }, 1000);
  }

  // Actions
  async function loginWithGoogle() {
    loading.value = true;
    error.value = null;

    try {
      // console.log('🔐 [AuthStore] loginWithGoogle called');
      // console.log('🔐 [AuthStore] Calling supabaseAuthService.signInWithGoogle()');
      await supabaseAuthService.signInWithGoogle();
      // Note: The actual user data will be set via onAuthStateChange
      // when the OAuth callback completes
      // console.log('✅ [AuthStore] supabaseAuthService.signInWithGoogle() completed');
      
      // Track Google login attempt in Analytics
      try {
        await analytics.logEvent('login_attempt', { method: 'google' });
      } catch (err) {
        // Analytics might not be initialized yet
      }
    } catch (err: any) {
      console.error('❌ [AuthStore] Google login error:', err);
      error.value = err.message || 'Google login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function loginWithApple() {
    loading.value = true;
    error.value = null;

    try {
      console.log('🍎 [AuthStore] loginWithApple called');
      await supabaseAuthService.signInWithApple();
      // Note: The actual user data will be set via onAuthStateChange
      // when the OAuth callback completes
      
      // Track Apple login attempt in Analytics
      try {
        await analytics.logEvent('login_attempt', { method: 'apple' });
      } catch (err) {
        // Analytics might not be initialized yet
      }
    } catch (err: any) {
      console.error('❌ [AuthStore] Apple login error:', err);
      error.value = err.message || 'Apple login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function loginWithEmailPassword(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const signedInUser = await supabaseAuthService.signInWithEmailPassword(email, password);
      user.value = signedInUser;
      isAuthenticated.value = true;
      
      // Track analytics. The login/sign_up event itself is emitted centrally by
      // the onAuthStateChange('SIGNED_IN') handler (which sees every provider),
      // so we only set the user id here to avoid double-counting.
      try {
        await analytics.setUserId(signedInUser.id);
      } catch (err) {
        // Analytics might not be initialized yet
      }

      // Save to localStorage (user est une ref : utiliser .value pour éviter "Converting circular structure to JSON")
      localStorage.setItem('harmonia_user', JSON.stringify(signedInUser));
    } catch (err: any) {
      console.error('Email/password login error:', err);
      error.value = err.message || 'Invalid email or password';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function signUpWithEmailPassword(email: string, password: string, name?: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const signedUpUser = await supabaseAuthService.signUpWithEmailPassword(email, password, name);
      user.value = signedUpUser;
      isAuthenticated.value = true;
      
      // Track analytics. The sign_up event itself is emitted centrally by the
      // onAuthStateChange('SIGNED_IN') handler (which detects new accounts across
      // every provider), so we only set the user id here to avoid double-counting.
      try {
        await analytics.setUserId(signedUpUser.id);
      } catch (err) {
        // Analytics might not be initialized yet
      }

      // Save to localStorage
      localStorage.setItem('harmonia_user', JSON.stringify(signedUpUser));
    } catch (err: any) {
      console.error('Email/password signup error:', err);
      error.value = err.message || 'Sign up failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(email: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await supabaseAuthService.resetPassword(email);
      
      // Track analytics
      try {
        await analytics.logEvent('password_reset_requested');
      } catch (err) {
        // Analytics might not be initialized yet
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      error.value = err.message || 'Failed to send password reset email';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updatePassword(newPassword: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await supabaseAuthService.updatePassword(newPassword);
      
      // Track analytics
      try {
        await analytics.logEvent('password_updated');
      } catch (err) {
        // Analytics might not be initialized yet
      }
    } catch (err: any) {
      console.error('Update password error:', err);
      error.value = err.message || 'Failed to update password';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;
    error.value = null;

    try {
      // Track logout in Analytics before clearing user data
      try {
        await analytics.logEvent('logout');
        await analytics.setUserId(null);
      } catch (err) {
        // Analytics might not be initialized yet
      }

      // Remove this device's push token while the session is still valid
      // (RLS requires auth.uid()). Best-effort: never block logout on it.
      try {
        await pushTokenService.unregisterCurrentToken();
      } catch (err) {
        console.debug('[Auth Store] push token unregister skipped:', err);
      }

      await supabaseAuthService.logout();
      user.value = null;
      isAuthenticated.value = false;

      // Vider le localStorage des données liées au COMPTE (pour éviter qu'elles
      // ne soient préchargées si un autre compte se connecte ensuite), tout en
      // PRÉSERVANT les préférences device-level (popups d'info déjà vues, langue…)
      // qui doivent survivre à une déconnexion/reconnexion.
      clearAccountStorage();

      // Réinitialiser le contexte de l'application lors de la déconnexion
      const appContextStore = useAppContextStore();
      const userContextStore = useUserContextStore();
      appContextStore.resetContext();
      userContextStore.reset();
    } catch (err: any) {
      console.error('Logout error:', err);
      error.value = err.message || 'Logout failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateProfile(profileData: Partial<User>) {
    loading.value = true;
    error.value = null;

    try {
      if (!user.value) {
        throw new Error('No user logged in');
      }

      // Update user profile via Supabase
      const updatedUser = await supabaseAuthService.updateProfile({ ...user.value, ...profileData });
      user.value = updatedUser;

      // Save to localStorage
      localStorage.setItem('harmonia_user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (err: any) {
      console.error('Profile update error:', err);
      error.value = err.message || 'Profile update failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createAnonymousAccount(displayName?: string) {
    loading.value = true;
    error.value = null;

    try {
      const anonymousUser = await supabaseAuthService.createAnonymousUser(displayName);
      user.value = anonymousUser;
      isAuthenticated.value = true;
      localStorage.setItem('harmonia_user', JSON.stringify(anonymousUser));
      localStorage.setItem('harmonia_anonymous', 'true');
      return anonymousUser;
    } catch (err: any) {
      console.error('Anonymous account creation error:', err);
      error.value = err.message || 'Failed to create anonymous account';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createHouseholdThenAnonymousAccount(householdName: string, displayName?: string, language = 'fr') {
    loading.value = true;
    error.value = null;

    try {
      // console.log('Création utilisateur anonyme et foyer...');

      // Import the new onboarding service
      const { supabaseOnboardingService } = await import('@/services/supabase/onboarding.service');

      // Create user and household in one go
      const result = await supabaseOnboardingService.createUserAndHousehold(
        displayName || 'Nouveau parent',
        householdName,
        '',
        language
      );

      // console.log('Utilisateur et foyer créés avec succès:', result);

      // Update the state
      user.value = {
        id: result.user_id,
        name: result.user_name,
        email: '',
        photoURL: undefined,
        language: result.language || language,
        notification_preferences: {
          daily_reminder: false,
          point_assignments: false,
          reward_unlocked: false
        }
      };
      isAuthenticated.value = true;
      localStorage.setItem('harmonia_user', JSON.stringify(user.value));
      localStorage.setItem('harmonia_anonymous', 'true');

      // console.log('Onboarding terminé avec succès');
      return { user: user.value, household: result };
    } catch (err: any) {
      console.error('Erreur lors de l\'onboarding:', err);
      error.value = err.message || 'Échec de l\'onboarding';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function upgradeAnonymousToEmail(email: string, name?: string) {
    loading.value = true;
    error.value = null;

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        email: email.trim(),
        data: {
          display_name: name || user.value?.name,
          full_name: name || user.value?.name
        }
      });

      if (updateError) throw updateError;

      // Supabase enverra automatiquement un email de confirmation
      isLinkSent.value = true;
      lastMagicLinkEmail.value = email;
      magicLinkContext.value = 'signup';

    } catch (err: any) {
      console.error('Account upgrade error:', err);
      error.value = err.message || 'Échec de l\'upgrade du compte';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Removed: updatePassword, setPassword - passwordless authentication only

  async function deleteAnonymousAccount() {
    loading.value = true;
    error.value = null;

    try {
      await supabaseAuthService.deleteAnonymousAccount();
      user.value = null;
      isAuthenticated.value = false;
      localStorage.removeItem('harmonia_user');
      localStorage.removeItem('harmonia_anonymous');
    } catch (err: any) {
      console.error('Delete account error:', err);
      error.value = err.message || 'Failed to delete account';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Anonymous authentication methods
  async function signInAnonymously(): Promise<User> {
    loading.value = true;
    error.value = null;

    try {
      // console.log('🔐 [Auth Store] Starting anonymous sign in...');
      const anonymousUser = await authAnonymousService.signInAnonymously();

      user.value = anonymousUser;
      isAuthenticated.value = true;
      localStorage.setItem('harmonia_user', JSON.stringify(anonymousUser));
      localStorage.setItem('harmonia_anonymous', 'true');

      // console.log('✅ [Auth Store] Anonymous sign in successful');
      return anonymousUser;
    } catch (err: any) {
      console.error('❌ [Auth Store] Anonymous sign in failed:', err);
      error.value = err.message || 'Anonymous sign in failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function upgradeToPermanentAccount(email: string, name: string): Promise<void> {
    // Use the new passwordless upgrade method
    await upgradeAnonymousToEmail(email, name);
  }

  async function isCurrentUserAnonymous(): Promise<boolean> {
    try {
      return await authAnonymousService.isCurrentUserAnonymous();
    } catch (err) {
      console.error('❌ [Auth Store] Error checking anonymous status:', err);
      return false;
    }
  }

  // Computed properties for anonymous state
  const isAnonymous = computed(() => {
    return localStorage.getItem('harmonia_anonymous') === 'true' || user.value?.email === '';
  });

  // Getters
  const userData = computed(() => user.value);
  const authStatus = computed((): AuthState => ({
    user: user.value,
    isAuthenticated: isAuthenticated.value,
    loading: loading.value,
    error: error.value
  }));

  return {
    // State
    user,
    isAuthenticated,
    loading,
    error,

    // Actions
    loginWithGoogle,
    loginWithApple,
    loginWithEmailPassword,
    signUpWithEmailPassword,
    resetPassword,
    updatePassword,
    logout,
    updateProfile,
    createAnonymousAccount,
    createHouseholdThenAnonymousAccount,
    upgradeAnonymousToEmail,
    deleteAnonymousAccount,

    // Anonymous authentication
    signInAnonymously,
    upgradeToPermanentAccount,
    isCurrentUserAnonymous,

    // Getters
    userData,
    authStatus,
    isAnonymous,
    waitForAuth,
    waitForAuthState
  };

});
